// src/components/IntegrationsScreen.jsx
// Integrações premium.
// Google Agenda:
// GET    /psy/integracoes/google/status
// GET    /psy/integracoes/google/testar
// GET    /psy/integracoes/google/autorizar
// DELETE /psy/integracoes/google/desconectar

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  ArrowLeft,
  BellRing,
  CalendarDays,
  Info,
  Link2,
  Mail,
  Menu,
  PlugZap,
  RefreshCw,
  RotateCw,
  Trash2,
  Unplug,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/integrations.scss";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const GOOGLE_STATUS_URL =
  `${API_BASE_URL}/psy/integracoes/google/status`;

const GOOGLE_TEST_URL =
  `${API_BASE_URL}/psy/integracoes/google/testar`;

const GOOGLE_AUTHORIZE_URL =
  `${API_BASE_URL}/psy/integracoes/google/autorizar`;

const GOOGLE_DISCONNECT_URL =
  `${API_BASE_URL}/psy/integracoes/google/desconectar`;

function readStorage(keys = []) {
  for (const key of keys) {
    const value =
      sessionStorage.getItem(key) ||
      localStorage.getItem(key);

    if (
      value &&
      String(value).trim() !== ""
    ) {
      return String(value).trim();
    }
  }

  return "";
}

function getAuthToken() {
  return readStorage([
    "psy_token",
    "token",
  ]);
}

function getUserName() {
  return (
    readStorage([
      "psy_usuario",
      "psy_nome",
      "usuario",
      "user",
    ]) || "Usuário"
  );
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(
    "psy_token",
    token
  );

  sessionStorage.setItem(
    "psy_token",
    token
  );
}

function getGoogleEmail(status = {}) {
  return (
    status?.email ||
    status?.contaEmail ||
    status?.googleEmail ||
    status?.accountEmail ||
    status?.integracao?.email ||
    ""
  );
}

function getCalendarId(status = {}) {
  return (
    status?.calendarId ||
    status?.integracao?.calendarId ||
    "primary"
  );
}

function formatDateTimeBR(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

async function parseResponse(response) {
  const contentType =
    response.headers.get(
      "content-type"
    ) || "";

  if (
    contentType.includes(
      "application/json"
    )
  ) {
    return response
      .json()
      .catch(() => ({}));
  }

  const text =
    await response.text();

  return text
    ? { message: text }
    : {};
}

function getOAuthErrorMessage(reason) {
  const normalized =
    String(reason || "")
      .trim()
      .toLowerCase();

  if (
    normalized === "access_denied"
  ) {
    return "A autorização do Google foi cancelada.";
  }

  if (
    normalized ===
    "calendar_test_failed"
  ) {
    return "A conta foi autorizada, mas o acesso ao Google Agenda não pôde ser validado. Revise a conexão.";
  }

  if (
    normalized ===
    "invalid_callback"
  ) {
    return "O retorno do Google não contém os dados esperados. Tente conectar novamente.";
  }

  return "Não foi possível concluir a conexão com o Google Agenda.";
}

export default function IntegrationsScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * Evita processar o retorno do OAuth mais de uma vez
   * enquanto a tela estiver montada.
   */
  const oauthProcessedRef =
    useRef(false);

  const [sidebarOpen, setSidebarOpen] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [testing, setTesting] =
    useState(false);

  const [
    disconnecting,
    setDisconnecting,
  ] = useState(false);

  const [status, setStatus] =
    useState({
      conectado: false,
      operacional: false,
      requerReconexao: false,
    });

  const [
    confirmOpen,
    setConfirmOpen,
  ] = useState(false);

  const userName = useMemo(
    () => getUserName(),
    []
  );

  const connected =
    Boolean(status?.conectado);

  const operational =
    status?.operacional === true;

  const needsAttention =
    connected && !operational;

  const requiresReconnect =
    Boolean(
      status?.requerReconexao
    );

  const googleEmail =
    getGoogleEmail(status);

  const calendarId =
    getCalendarId(status);

  const lastSync =
    formatDateTimeBR(
      status?.ultimaSincronizacaoEm
    );

  const lastError =
    String(
      status?.ultimoErro ||
      status?.erro ||
      status?.message ||
      ""
    ).trim();

  const toast = useToast();

  const showMessage = useCallback(
    (type, text) => {
      const safeType =
        ["success", "warning", "error", "info"].includes(type)
          ? type
          : "info";

      const titles = {
        success: "Tudo certo",
        warning: "Atenção",
        error: "Não foi possível concluir",
        info: "Informação",
      };

      const notify = toast[safeType] || toast.info;

      notify({
        title: titles[safeType],
        message: String(text || "").trim(),
        duration: safeType === "error" ? 7000 : 5200,
      });
    },
    [toast]
  );

  const testGoogleConnection =
    useCallback(
      async (
        tokenAtual,
        currentStatus,
        {
          notify = false,
        } = {}
      ) => {
        setTesting(true);

        try {
          const response =
            await fetch(
              GOOGLE_TEST_URL,
              {
                method: "GET",
                headers: {
                  Authorization:
                    `Bearer ${tokenAtual}`,
                  Accept:
                    "application/json",
                },
              }
            );

          saveAuthToken(
            response.headers.get(
              "x-new-token"
            )
          );

          const data =
            await parseResponse(
              response
            );

          if (
            response.status === 401
          ) {
            throw new Error(
              data?.message ||
              "Sua sessão expirou. Entre novamente para continuar."
            );
          }

          if (
            response.status === 403
          ) {
            throw new Error(
              data?.message ||
              "A integração com o Google Agenda está disponível apenas no plano Premium."
            );
          }

          if (response.ok) {
            const nextStatus = {
              ...currentStatus,
              ...data,
              conectado: true,
              operacional: true,
              requerReconexao: false,
              ultimoErro: null,
              ultimoErroEm: null,
            };

            setStatus(nextStatus);

            if (notify) {
              showMessage(
                "success",
                data?.message ||
                  "Conexão com o Google Agenda validada com sucesso."
              );
            }

            return nextStatus;
          }

          const nextStatus = {
            ...currentStatus,
            conectado: true,
            operacional: false,
            requerReconexao:
              Boolean(
                data?.requerReconexao
              ),
            ultimoErro:
              data?.message ||
              currentStatus
                ?.ultimoErro ||
              "A conexão com o Google Agenda precisa ser revisada.",
            ultimoErroEm:
              new Date().toISOString(),
          };

          setStatus(nextStatus);

          if (notify) {
            showMessage(
              "warning",
              nextStatus.ultimoErro
            );
          }

          return nextStatus;
        } catch (error) {
          const nextStatus = {
            ...currentStatus,
            conectado:
              Boolean(
                currentStatus
                  ?.conectado
              ),
            operacional: false,
            ultimoErro:
              error?.message ||
              "Não foi possível validar a conexão com o Google Agenda.",
          };

          setStatus(nextStatus);

          if (notify) {
            showMessage(
              "error",
              nextStatus.ultimoErro
            );
          }

          return nextStatus;
        } finally {
          setTesting(false);
        }
      },
      [showMessage]
    );

  const loadStatus = useCallback(
    async ({
      notify = false,
    } = {}) => {
      const tokenAtual =
        getAuthToken();

      if (!tokenAtual) {
        const nextStatus = {
          conectado: false,
          operacional: false,
          requerReconexao: false,
          ultimoErro:
            "Sua sessão não foi encontrada. Entre novamente para continuar.",
        };

        setStatus(nextStatus);
        setLoading(false);

        if (notify) {
          showMessage(
            "error",
            nextStatus.ultimoErro
          );
        }

        return nextStatus;
      }

      setLoading(true);

      try {
        const response =
          await fetch(
            GOOGLE_STATUS_URL,
            {
              method: "GET",
              headers: {
                Authorization:
                  `Bearer ${tokenAtual}`,
                Accept:
                  "application/json",
              },
            }
          );

        saveAuthToken(
          response.headers.get(
            "x-new-token"
          )
        );

        const data =
          await parseResponse(
            response
          );

        if (
          response.status === 401
        ) {
          throw new Error(
            data?.message ||
            "Sua sessão expirou. Entre novamente para continuar."
          );
        }

        if (
          response.status === 403
        ) {
          throw new Error(
            data?.message ||
            "A integração com o Google Agenda está disponível apenas no plano Premium."
          );
        }

        if (!response.ok) {
          throw new Error(
            data?.message ||
            data?.erro ||
            "Não foi possível consultar a integração com o Google."
          );
        }

        const baseStatus = {
          ...data,
          conectado:
            Boolean(
              data?.conectado
            ),
          operacional:
            data?.conectado
              ? null
              : false,
          requerReconexao:
            Boolean(
              data?.requerReconexao
            ),
        };

        setStatus(baseStatus);

        if (
          !baseStatus.conectado
        ) {
          if (notify) {
            showMessage(
              "info",
              "Nenhuma conta Google está conectada."
            );
          }

          return baseStatus;
        }

        /*
         * /status confirma o vínculo salvo.
         * /testar confirma que as credenciais funcionam
         * de verdade na Calendar API.
         */
        return await testGoogleConnection(
          tokenAtual,
          baseStatus,
          { notify }
        );
      } catch (error) {
        const nextStatus = {
          conectado: false,
          operacional: false,
          requerReconexao: false,
          ultimoErro:
            error?.message ||
            "Erro ao consultar a integração com o Google.",
        };

        setStatus(nextStatus);

        if (notify) {
          showMessage(
            "error",
            nextStatus.ultimoErro
          );
        }

        return nextStatus;
      } finally {
        setLoading(false);
      }
    },
    [
      showMessage,
      testGoogleConnection,
    ]
  );

  /*
   * Carregamento normal da tela.
   *
   * Quando existe retorno do OAuth na URL, quem faz a
   * atualização é exclusivamente o effect seguinte.
   * Isso evita duas execuções simultâneas de loadStatus().
   */
  useEffect(() => {
    const params =
      new URLSearchParams(
        location.search
      );

    const google =
      params.get("google");

    if (!google) {
      loadStatus();
    }

  }, [
    loadStatus,
    location.search,
  ]);

  /*
   * Processa uma única vez o retorno do OAuth Google.
   *
   * O backend só retorna google=success depois de:
   * - validar o state;
   * - trocar o code por tokens;
   * - salvar a ConnectedAccount;
   * - testar uma chamada real na Calendar API.
   *
   * Aqui apenas recarregamos o estado persistido e só
   * mostramos sucesso depois dessa atualização terminar.
   */
  useEffect(() => {
    const params =
      new URLSearchParams(
        location.search
      );

    const google =
      params.get("google");

    if (
      !google ||
      oauthProcessedRef.current
    ) {
      return;
    }

    oauthProcessedRef.current = true;

    const reason =
      params.get("reason");

    let cancelled = false;

    const processOAuthReturn =
      async () => {
        if (google === "success") {
          const nextStatus =
            await loadStatus();

          if (cancelled) {
            return;
          }

          if (
            nextStatus?.conectado &&
            nextStatus?.operacional
          ) {
            showMessage(
              "success",
              "Google Agenda conectado e validado com sucesso."
            );
          } else {
            showMessage(
              "warning",
              nextStatus?.ultimoErro ||
              "A autorização foi concluída, mas a conexão ainda precisa ser validada."
            );
          }
        } else {
          showMessage(
            "error",
            getOAuthErrorMessage(
              reason
            )
          );
        }

        if (cancelled) {
          return;
        }

        /*
         * Remove ?google=... da URL sem remontar a tela
         * nem criar um novo ciclo de OAuth.
         */
        navigate(
          location.pathname,
          {
            replace: true,
          }
        );
      };

    processOAuthReturn();

    return () => {
      cancelled = true;
    };
  }, [
    loadStatus,
    location.pathname,
    location.search,
    navigate,
    showMessage,
  ]);

  useEffect(() => {
    if (!confirmOpen) {
      return undefined;
    }

    function handleKeyDown(
      event
    ) {
      if (
        event.key === "Escape"
      ) {
        setConfirmOpen(
          false
        );
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () =>
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
  }, [confirmOpen]);

  const connectGoogle =
    async () => {
      const tokenAtual =
        getAuthToken();

      if (!tokenAtual) {
        showMessage(
          "error",
          "Sua sessão expirou. Entre novamente para conectar o Google."
        );
        return;
      }

      setLoading(true);

      try {
        const response =
          await fetch(
            GOOGLE_AUTHORIZE_URL,
            {
              method: "GET",
              headers: {
                Authorization:
                  `Bearer ${tokenAtual}`,
                Accept:
                  "application/json",
              },
            }
          );

        saveAuthToken(
          response.headers.get(
            "x-new-token"
          )
        );

        const data =
          await parseResponse(
            response
          );

        if (
          response.status === 401
        ) {
          throw new Error(
            data?.message ||
            "Sua sessão expirou. Entre novamente para continuar."
          );
        }

        if (
          response.status === 403
        ) {
          throw new Error(
            data?.message ||
            "A integração com o Google Agenda está disponível apenas no plano Premium."
          );
        }

        if (!response.ok) {
          throw new Error(
            data?.message ||
            data?.erro ||
            "Não foi possível iniciar a conexão com o Google."
          );
        }

        if (!data?.url) {
          throw new Error(
            "A API não retornou a URL de autorização do Google."
          );
        }

        window.location.assign(
          data.url
        );
      } catch (error) {
        showMessage(
          "error",
          error?.message ||
          "Erro ao conectar o Google Agenda."
        );

        setLoading(false);
      }
    };

  const disconnectGoogle =
    async () => {
      const tokenAtual =
        getAuthToken();

      if (
        !tokenAtual ||
        disconnecting
      ) {
        if (!tokenAtual) {
          showMessage(
            "error",
            "Sua sessão expirou. Entre novamente para desconectar o Google."
          );
        }

        return;
      }

      setDisconnecting(true);

      try {
        const response =
          await fetch(
            GOOGLE_DISCONNECT_URL,
            {
              method:
                "DELETE",
              headers: {
                Authorization:
                  `Bearer ${tokenAtual}`,
                Accept:
                  "application/json",
              },
            }
          );

        saveAuthToken(
          response.headers.get(
            "x-new-token"
          )
        );

        const data =
          await parseResponse(
            response
          );

        if (
          response.status === 401
        ) {
          throw new Error(
            data?.message ||
            "Sua sessão expirou. Entre novamente para continuar."
          );
        }

        if (
          response.status === 403
        ) {
          throw new Error(
            data?.message ||
            "A integração com o Google Agenda está disponível apenas no plano Premium."
          );
        }

        if (!response.ok) {
          throw new Error(
            data?.message ||
            data?.erro ||
            "Não foi possível desconectar o Google Agenda."
          );
        }

        setStatus({
          conectado: false,
          operacional: false,
          requerReconexao: false,
        });

        setConfirmOpen(
          false
        );

        showMessage(
          "success",
          "Google Agenda desconectado com sucesso."
        );
      } catch (error) {
        showMessage(
          "error",
          error?.message ||
          "Erro ao desconectar o Google Agenda."
        );
      } finally {
        setDisconnecting(
          false
        );
      }
    };

  const statusLabel =
    loading
      ? "Consultando"
      : operational
        ? "Operacional"
        : needsAttention
          ? "Requer atenção"
          : "Desconectado";

  const generalStatus =
    loading
      ? "Consultando..."
      : operational
        ? "1 integração operacional"
        : needsAttention
          ? "1 integração requer atenção"
          : "Nenhuma integração ativa";

  return (
    <div className="int-page">
      <div
        className="int-bg"
        aria-hidden="true"
      >
        <span className="int-bg__orb int-bg__orb--one" />
        <span className="int-bg__orb int-bg__orb--two" />
        <span className="int-bg__orb int-bg__orb--three" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() =>
          setSidebarOpen(false)
        }
      />

      <header className="int-topbar">
        <button
          className="int-icon-btn"
          type="button"
          onClick={() =>
            setSidebarOpen(true)
          }
          aria-label="Abrir menu"
        >
          <Menu size={19} />
        </button>

        <div className="int-brand">
          <img
            src={Logo}
            alt="PsyAssist"
            className="int-brand__logo"
          />

          <div className="int-brand__text">
            <strong>
              Integrações
            </strong>
            <span>
              Conta • {userName}
            </span>
          </div>
        </div>

        <div className="int-top-actions">
          <button
            className="int-btn int-btn--ghost int-btn--desktop"
            type="button"
            onClick={() =>
              navigate(
                "/settings"
              )
            }
          >
            <ArrowLeft
              size={16}
            />
            Configurações
          </button>

          <button
            className="int-btn int-btn--primary"
            type="button"
            onClick={() =>
              navigate("/")
            }
          >
            <Link2 size={16} />
            Início
          </button>
        </div>
      </header>

      <main className="int-main">
        <section className="int-hero">
          <div>
            <span className="int-kicker">
              <Link2 size={14} />
              Conexões externas
            </span>

            <h1>
              Integrações
            </h1>

            <p>
              Conecte o PsyAssist aos serviços usados na sua rotina e mantenha
              seus agendamentos organizados automaticamente.
            </p>
          </div>

          <aside className="int-hero-card">
            <div
              className="int-hero-card__icon"
              aria-hidden="true"
            >
              <Link2 size={22} />
            </div>

            <div>
              <small>
                Status geral
              </small>

              <strong>
                {generalStatus}
              </strong>

              <span>
                {needsAttention
                  ? "A conta está vinculada, mas a conexão operacional precisa ser revisada."
                  : "Gerencie suas conexões com segurança em um só lugar."}
              </span>
            </div>
          </aside>
        </section>

        <section className="int-section">
          <div className="int-section__head">
            <div>
              <span>
                Agenda
              </span>

              <h2>
                Google Agenda
              </h2>

              <p>
                Envie automaticamente os agendamentos do PsyAssist para sua
                agenda Google.
              </p>
            </div>

            <span
              className={`int-status ${
                operational
                  ? "int-status--on"
                  : "int-status--off"
              }`}
            >
              <span
                aria-hidden="true"
              />
              {statusLabel}
            </span>
          </div>

          <article className="int-card">
            <div className="int-card__header">
              <div
                className="int-google-logo"
                aria-hidden="true"
              >
                <span>G</span>
              </div>

              <div className="int-card__title">
                <h3>
                  Google Agenda
                </h3>

                <p>
                  Criação, atualização e exclusão automática de eventos.
                </p>
              </div>
            </div>

            <div className="int-features">
              <div className="int-feature">
                <span aria-hidden="true">
                  <CalendarDays
                    size={19}
                  />
                </span>

                <div>
                  <strong>
                    Sincronização automática
                  </strong>

                  <p>
                    Novos agendamentos são enviados para o Google Agenda.
                  </p>
                </div>
              </div>

              <div className="int-feature">
                <span aria-hidden="true">
                  <RotateCw
                    size={19}
                  />
                </span>

                <div>
                  <strong>
                    Atualizações em tempo real
                  </strong>

                  <p>
                    Alterações de horário, serviço e duração atualizam o mesmo
                    evento.
                  </p>
                </div>
              </div>

              <div className="int-feature">
                <span aria-hidden="true">
                  <Trash2
                    size={19}
                  />
                </span>

                <div>
                  <strong>
                    Cancelamentos sincronizados
                  </strong>

                  <p>
                    Eventos são removidos quando o agendamento é cancelado.
                  </p>
                </div>
              </div>

              <div className="int-feature">
                <span aria-hidden="true">
                  <BellRing
                    size={19}
                  />
                </span>

                <div>
                  <strong>
                    Lembrete automático
                  </strong>

                  <p>
                    O evento é criado com lembrete de 30 minutos.
                  </p>
                </div>
              </div>
            </div>

            <div className="int-account">
              <div>
                <small>
                  Conta Google conectada
                </small>

                <strong>
                  {loading
                    ? "Consultando conta..."
                    : connected
                      ? googleEmail ||
                        "E-mail ainda não identificado"
                      : "Nenhuma conta conectada"}
                </strong>

                <span>
                  {connected
                    ? googleEmail
                      ? `Agenda utilizada: ${calendarId}`
                      : "A conexão está ativa, mas o e-mail não pôde ser identificado. Reconecte a conta Google uma vez para atualizar essa informação."
                    : "O e-mail é escolhido na tela oficial do Google durante a conexão."}
                </span>

                {connected &&
                lastSync ? (
                  <span>
                    Última sincronização: {lastSync}
                  </span>
                ) : null}
              </div>

              <div
                className="int-account__icon"
                aria-hidden="true"
              >
                <Mail size={20} />
              </div>
            </div>

            {needsAttention ? (
              <div className="int-note">
                <span aria-hidden="true">
                  <Info size={18} />
                </span>

                <p>
                  <strong>
                    A conexão com o Google Agenda requer atenção.
                  </strong>
                  {" "}
                  {lastError ||
                    (requiresReconnect
                      ? "Reconecte sua conta Google para renovar as permissões."
                      : "Tente validar a conexão novamente.")}
                </p>
              </div>
            ) : (
              <div className="int-note">
                <span aria-hidden="true">
                  <Info size={18} />
                </span>

                <p>
                  O e-mail da conta Google não precisa ser digitado manualmente.
                  Ao clicar em conectar, o Google solicitará que você escolha a
                  conta que será usada pelo PsyAssist.
                </p>
              </div>
            )}

            <div className="int-card__actions">
              <button
                className="int-btn int-btn--ghost-dark"
                type="button"
                onClick={() =>
                  loadStatus({
                    notify: true,
                  })
                }
                disabled={
                  loading ||
                  testing ||
                  disconnecting
                }
              >
                <RefreshCw
                  size={16}
                  className={
                    loading ||
                    testing
                      ? "int-spin"
                      : ""
                  }
                />

                {loading ||
                testing
                  ? "Validando..."
                  : "Validar conexão"}
              </button>

              {connected &&
              needsAttention ? (
                <button
                  className="int-btn int-btn--google"
                  type="button"
                  onClick={
                    connectGoogle
                  }
                  disabled={
                    loading ||
                    testing
                  }
                >
                  <PlugZap
                    size={16}
                  />
                  Reconectar Google
                </button>
              ) : null}

              {connected ? (
                <button
                  className="int-btn int-btn--danger"
                  type="button"
                  onClick={() =>
                    setConfirmOpen(
                      true
                    )
                  }
                  disabled={
                    disconnecting
                  }
                >
                  <Unplug
                    size={16}
                  />
                  Desconectar Google
                </button>
              ) : (
                <button
                  className="int-btn int-btn--google"
                  type="button"
                  onClick={
                    connectGoogle
                  }
                  disabled={loading}
                >
                  <PlugZap
                    size={16}
                  />
                  {loading
                    ? "Conectando..."
                    : "Conectar Google Agenda"}
                </button>
              )}
            </div>
          </article>
        </section>
      </main>

      {confirmOpen ? (
        <div
          className="int-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="int-disconnect-title"
          onClick={() => {
            if (
              !disconnecting
            ) {
              setConfirmOpen(
                false
              );
            }
          }}
        >
          <div
            className="int-dialog"
            onClick={(
              event
            ) =>
              event.stopPropagation()
            }
          >
            <div
              className="int-dialog__icon"
              aria-hidden="true"
            >
              <Unplug
                size={24}
              />
            </div>

            <div
              id="int-disconnect-title"
              className="int-dialog__title"
            >
              Desconectar Google Agenda?
            </div>

            <div className="int-dialog__desc">
              Novos agendamentos deixarão de ser enviados ao Google. Os
              agendamentos do PsyAssist continuarão normalmente.
            </div>

            <div className="int-dialog__actions">
              <button
                className="int-btn int-btn--ghost-dark"
                type="button"
                onClick={() =>
                  setConfirmOpen(
                    false
                  )
                }
                disabled={
                  disconnecting
                }
              >
                Cancelar
              </button>

              <button
                className="int-btn int-btn--danger"
                type="button"
                onClick={
                  disconnectGoogle
                }
                disabled={
                  disconnecting
                }
              >
                {disconnecting
                  ? "Desconectando..."
                  : "Desconectar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
